/**
 * useSelection — Generic multi-select hook
 *
 * Tier 3 — Platform Packages (@plerity/react-shared)
 * Purpose: Manage a selection set for list views with bulk operations.
 *          Generalized over the id type (number | string) from the
 *          byte-identical app-specific versions in email and ops.
 *
 * @example
 * ```tsx
 * const { selected, toggle, toggleAll, clear, isSelected, count } =
 *   useSelection<number>(allContactIds);
 * ```
 */

import { useCallback, useMemo, useState } from "react";

export interface UseSelectionReturn<T extends number | string> {
  /** Current selection as a Set for O(1) membership tests */
  selected: Set<T>;
  /** Toggle a single id in/out of the selection */
  toggle: (id: T) => void;
  /**
   * Toggle all ids.  If `allIds` was provided to the hook, uses that list;
   * otherwise a no-op (there is nothing to select "all" of).
   * When all are already selected, clears the selection.
   */
  toggleAll: () => void;
  /** Remove every id from the selection */
  clear: () => void;
  /** O(1) membership check */
  isSelected: (id: T) => boolean;
  /** Selection as a stable array (memoized) */
  selectedArray: T[];
  /** Number of selected ids */
  count: number;
  /** True when every id in `allIds` is selected (false when `allIds` is not provided) */
  allSelected: boolean;
  /** True when at least one id is selected but not all */
  someSelected: boolean;
}

/**
 * Generic multi-select hook.
 *
 * @param allIds - Optional list of all available ids, used by `toggleAll`.
 */
export function useSelection<T extends number | string>(allIds?: T[]): UseSelectionReturn<T> {
  const [selected, setSelected] = useState<Set<T>>(new Set<T>());

  const toggle = useCallback((id: T) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (!allIds || allIds.length === 0) return;
    setSelected((prev) => {
      const allSelectedNow = allIds.length > 0 && allIds.every((id) => prev.has(id));
      return allSelectedNow ? new Set<T>() : new Set<T>(allIds);
    });
  }, [allIds]);

  const clear = useCallback(() => {
    setSelected(new Set<T>());
  }, []);

  const isSelected = useCallback((id: T) => selected.has(id), [selected]);

  const selectedArray = useMemo(() => Array.from(selected), [selected]);

  const count = selected.size;

  const allSelected = useMemo(
    () => allIds !== undefined && allIds.length > 0 && allIds.every((id) => selected.has(id)),
    [allIds, selected]
  );

  const someSelected = useMemo(() => count > 0 && !allSelected, [count, allSelected]);

  return {
    selected,
    toggle,
    toggleAll,
    clear,
    isSelected,
    selectedArray,
    count,
    allSelected,
    someSelected,
  };
}
